import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Globe, Building2, User, Mail, Phone, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { API } from "../../App";

const INDUSTRIES = [
  { value: "hospitality", label: "Hospitality" },
  { value: "trades", label: "Trades" },
  { value: "health_wellness", label: "Health & Wellness" },
  { value: "professional_services", label: "Professional Services" },
  { value: "retail", label: "Retail" },
  { value: "other", label: "Other" }
];

const COMPANY_SIZES = [
  { value: "", label: "Auto-detect" },
  { value: "solo_micro", label: "Solo / Micro (1-5)" },
  { value: "small", label: "Small (6-20)" },
  { value: "medium", label: "Medium (21-100)" },
  { value: "large", label: "Large (100+)" }
];

const LeadEngineAdd = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    website_url: "",
    industry: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
    company_size_override: "",
    intent_override: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.website_url || !formData.industry) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        company_size_override: formData.company_size_override || null,
        intent_override: formData.intent_override ? parseInt(formData.intent_override) : null
      };
      
      const response = await axios.post(`${API}/lead-engine/prospects`, payload);
      toast.success("Prospect added");
      navigate(`/admin/lead-engine/${response.data.prospect_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add prospect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Add Prospect">
      <div className="max-w-2xl">
        <Link to="/admin/lead-engine" className="inline-flex items-center gap-2 text-sm text-[#8A8A8A] hover:text-[#F5F4EF] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Lead Engine
        </Link>

        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
          <h2 className="text-lg font-bold mb-6">Add New Prospect</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-[#8A8A8A] uppercase tracking-wide">Company Information</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="input-field pl-10 w-full"
                    placeholder="Acme Ltd"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Website URL <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                  <input
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    className="input-field pl-10 w-full"
                    placeholder="www.example.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Industry <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="input-field w-full"
                    required
                  >
                    <option value="">Select industry...</option>
                    {INDUSTRIES.map((ind) => (
                      <option key={ind.value} value={ind.value}>{ind.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Company Size</label>
                  <select
                    name="company_size_override"
                    value={formData.company_size_override}
                    onChange={handleChange}
                    className="input-field w-full"
                  >
                    {COMPANY_SIZES.map((size) => (
                      <option key={size.value} value={size.value}>{size.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 pt-4 border-t border-[#2E2E2E]">
              <h3 className="text-sm font-medium text-[#8A8A8A] uppercase tracking-wide">Contact (Optional)</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">Contact Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                  <input
                    type="text"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    className="input-field pl-10 w-full"
                    placeholder="John Smith"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      className="input-field pl-10 w-full"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      className="input-field pl-10 w-full"
                      placeholder="+44 20 1234 5678"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Notes & Intent */}
            <div className="space-y-4 pt-4 border-t border-[#2E2E2E]">
              <h3 className="text-sm font-medium text-[#8A8A8A] uppercase tracking-wide">Additional</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field w-full resize-none"
                  rows={3}
                  placeholder="Any relevant context about this prospect..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Intent Override (0-100)
                  <span className="text-[#8A8A8A] font-normal ml-2">Leave blank for auto-scoring</span>
                </label>
                <input
                  type="number"
                  name="intent_override"
                  value={formData.intent_override}
                  onChange={handleChange}
                  className="input-field w-32"
                  min="0"
                  max="100"
                  placeholder="50"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? "Adding..." : "Add Prospect"}
              </button>
              <Link to="/admin/lead-engine" className="btn-secondary">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LeadEngineAdd;
